import createIri from '@/lib/helpers/createIri.ts'
import { IssueTypeT } from '@/components/common/menu/lib/types/navigationDataTypes.ts'

// Remembers, per user and per stage, the issue type the user last created an
// issue with, so the global "Report" entry point opens on it. Device-local by
// design: it is a convenience default, never a source of truth, so every read
// tolerates a missing, foreign or corrupt value and every access survives
// storage that throws (private mode, quota, disabled site data).

const KEY_PREFIX = `buildlog.lastUsedIssueType.v1`

// The only value shape ever written: an issue type IRI as built by createIri.
const ISSUE_TYPE_IRI_RE = /^\/issue_types\/\d+$/

type IdT = string | number

const isId = (id: IdT | null | undefined): id is IdT => id != null && id !== ``

export function lastUsedIssueTypeKey(userId: IdT, projectStageId: IdT): string {
  return `${KEY_PREFIX}:${userId}:${projectStageId}`
}

export function readLastUsedIssueType(
  userId: IdT | null | undefined,
  projectStageId: IdT | null | undefined,
): string | null {
  if (!isId(userId) || !isId(projectStageId)) return null
  try {
    const raw = localStorage.getItem(lastUsedIssueTypeKey(userId, projectStageId))
    if (raw === null) return null
    const value: unknown = JSON.parse(raw)
    return typeof value === `string` && ISSUE_TYPE_IRI_RE.test(value) ? value : null
  } catch {
    return null
  }
}

export function writeLastUsedIssueType(
  userId: IdT | null | undefined,
  projectStageId: IdT | null | undefined,
  issueTypeIri: string | null | undefined,
): void {
  if (!isId(userId) || !isId(projectStageId)) return
  if (!issueTypeIri || !ISSUE_TYPE_IRI_RE.test(issueTypeIri)) return
  try {
    localStorage.setItem(lastUsedIssueTypeKey(userId, projectStageId), JSON.stringify(issueTypeIri))
  } catch {
    // Best effort: losing the default only means the next open falls back.
  }
}

/** Only an explicit `true` grants create; `null` and a missing field do not. */
export function isCreatableIssueType(issueType: Pick<IssueTypeT, 'currentUserCanCreate'>) {
  return issueType.currentUserCanCreate === true
}

/**
 * The issue type the global "Report" entry point opens on: the last one used
 * in this stage if still creatable, else the current one if creatable, else
 * the first creatable type. Null when the user can create nothing here.
 */
export function resolveGlobalIssueTypeIri({
  issueTypes,
  lastUsedIri,
  currIssueTypeIri,
}: {
  issueTypes: IssueTypeT[]
  lastUsedIri: string | null
  currIssueTypeIri: string | null
}): string | null {
  const creatableIris = issueTypes
    .filter(isCreatableIssueType)
    .map(issueType => createIri(`issue_types`, issueType.id))
  if (lastUsedIri && creatableIris.includes(lastUsedIri)) return lastUsedIri
  if (currIssueTypeIri && creatableIris.includes(currIssueTypeIri)) return currIssueTypeIri
  return creatableIris[0] ?? null
}
